import { z } from "zod";
import { REMOTE_DESKTOP_EXECUTE_MODEL_TIERS } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Mirrors the backend operator `operatorNeedsContextSchema`
 * (src/core/ai/agents/remote-desktop-operator/types.ts) — duplicated here
 * because `packages/*` cannot import from `backend`.
 */
const operatorNeedsContextSchema = z.object({
  question: z.string(),
  operatorSummary: z.string(),
});

export const remoteDesktopExecuteStep = defineStepConfig({
  type: "remote_desktop_execute",
  name: "Desktop Computer Use",
  description:
    "Execute a natural-language instruction on the current remote desktop session via computer use. Requires an active session created by a Login to Remote Desktop step earlier in the run.",
  category: "Computer Use",

  inputSchema: z.object({
    instruction: z
      .string()
      .min(1)
      .describe("Instruction to execute on the remote desktop"),
    sessionId: z
      .string()
      .optional()
      .describe("Optional explicit session ID override"),
    maxIterations: z
      .number()
      .int()
      .positive()
      .max(200)
      .optional()
      .describe("Maximum number of computer-use iterations"),
    resumeSummary: z
      .string()
      .max(2000)
      .optional()
      .describe(
        "If a prior call returned `needsContext`, pass its `operatorSummary` here so the operator resumes with prior-run state.",
      ),
  }),

  outputSchema: z.object({
    success: z.boolean(),
    message: z.string().nullable(),
    error: z.string().nullable(),
    iterationsUsed: z.number().nullable(),
    needsContext: operatorNeedsContextSchema
      .nullable()
      .describe(
        "Set when the operator halted to request information only the orchestrating agent holds.",
      ),
  }),

  configSchema: z.object({
    /**
     * Computer-use model tier. When omitted, the orchestrating agent
     * definition's configured model is used.
     */
    model: z.enum(REMOTE_DESKTOP_EXECUTE_MODEL_TIERS).optional(),
    /**
     * In-desktop application credentials (referenced by id). Resolved by the
     * step handler and passed to the operator so it can log into apps running
     * inside the remote desktop. When omitted, falls back to the orchestrating
     * agent definition's configured application credentials.
     */
    applicationCredentials: z
      .object({
        credentialIds: z.array(z.string().uuid()).default([]),
      })
      .optional(),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 1,
    },
  },
});
